import {
  IConferenceQueryResult,
  IConferenceRoles,
  IConferenceAccessSettings,
  IQueryResponse,
  IConferenceStatusResponse,
} from "taskpane/models/interfaces";
import useQueryStartSession, { IStartSession, IStartSessionResponse } from "./useQueryStartSession";
import { getRandomRequestId, getRolesBySettings } from "taskpane/utils";
import useQuery from "./useQuery";
import useQueryEndSession from "./useQueryEndSession";
import { ConferenceStatusCode, QueryType } from "taskpane/models/enums";
import { getQueryErrorDeauthorization } from "taskpane/resources";
import getQueryErrorTryAgainLater from "taskpane/resources/getQueryErrorTryAgainLater";
import { OnReady } from "taskpane/models/types";

interface IUpdateConferencePostParams {
  reqId: string;
  aimsid: string;
  params: {
    id: string;
    roles: IConferenceRoles;
  };
}

export interface IUpdateConference extends IStartSession {
  settings: IConferenceAccessSettings;
  conferenceId: string;
}

const useQueryUpdateConference = (
  params: IUpdateConference,
  onReady: OnReady<IConferenceQueryResult<IStartSessionResponse | IConferenceStatusResponse>>
) => {
  const { token, email, apiUrl, conferenceId, settings } = params;
  const startSessionParams: IStartSession = {
    token,
    email,
    apiUrl,
  };

  useQueryStartSession(startSessionParams, (responseStartSession: IConferenceQueryResult<IStartSessionResponse>) => {
    if (responseStartSession.errorMessage) {
      return onReady(responseStartSession);
    }

    const aimsid: string = responseStartSession.response.response.data.aimsid;
    const url: string = `${apiUrl}/rapi/conference/update`;
    const postParams: IUpdateConferencePostParams = {
      reqId: getRandomRequestId(),
      aimsid: aimsid,
      params: {
        id: conferenceId,
        roles: getRolesBySettings(settings),
      },
    };

    useQuery(
      url,
      "POST",
      (result: IQueryResponse<IConferenceStatusResponse>) => {
        const response: IConferenceQueryResult<IConferenceStatusResponse> = {
          queryType: QueryType.updateConference,
          ...result,
        };

        if (result.errorMessage) {
          return onReady(response);
        }

        const statusCode = response.response.status.code;

        if (statusCode === ConferenceStatusCode.notAuthorized) {
          return onReady({
            ...response,
            errorMessage: getQueryErrorDeauthorization(),
          });
        } else if (statusCode !== ConferenceStatusCode.succeeded) {
          return onReady({
            ...response,
            errorMessage: getQueryErrorTryAgainLater("при обновлении конференции"),
          });
        }

        useQueryEndSession({
          aimsid: aimsid,
          apiUrl: apiUrl,
        });

        onReady(response);
      },
      { data: postParams }
    );
  });
};

export default useQueryUpdateConference;
